/**
 * HTTP service for the budgets feature (PX-W6.4): the five endpoints of
 * `BudgetController` minus the portfolio roll-up this ticket does not surface
 * (`server/conf/routes`, verified 2026-09-04). `/{orgId}` resolves on the
 * transport; paths address the project by SLUG.
 *
 * The active-budget GET answers 404 `budget.entry.not-found` when the project
 * has no budget — the slice reads that as `activeMissing` (absence is a fact,
 * I-7), never as an error, never as zero.
 */

import { apiClient } from '@/services'

import type { BudgetVsActualView, ProjectBudget, PutBudgetInput } from '../types'

export const budgetServiceHttp = {
  /** The active revision — 404 when none was ever agreed. */
  active(projectId: string): Promise<ProjectBudget> {
    return apiClient.get<ProjectBudget>(`/{orgId}/projects/${projectId}/budget`)
  },

  /** Agree a NEW revision (supersede-then-insert; `revision`/`status` are server-stamped). */
  putRevision(projectId: string, input: PutBudgetInput): Promise<ProjectBudget> {
    return apiClient.put<ProjectBudget>(`/{orgId}/projects/${projectId}/budget`, input)
  },

  /** Every revision, newest first; an empty list = never budgeted (a fact). */
  history(projectId: string): Promise<ProjectBudget[]> {
    return apiClient.get<ProjectBudget[]>(`/{orgId}/projects/${projectId}/budget/history`)
  },

  /** The roll-up — always 200; `hasBudget:false` keeps the hours half. */
  rollup(projectId: string): Promise<BudgetVsActualView> {
    return apiClient.get<BudgetVsActualView>(`/{orgId}/projects/${projectId}/budget-vs-actual`)
  }
}
